import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './dialog';
import { Button } from './button';
import { AlertTriangle, Loader2, Link2, Trash2 } from 'lucide-react';

/**
 * DeleteConfirmationModal - Styled confirmation dialog for delete operations
 * Supports both simple deletes and cascade deletes with linked items warning
 */
export const DeleteConfirmationModal = ({
  open,
  onOpenChange,
  title = "Confirm Delete",
  description = "Are you sure you want to delete this item? This action cannot be undone.",
  itemName = "",
  itemType = "item",
  onConfirm,
  loading = false,
  destructive = true,
  // Cascade delete props
  linkedItems = [],
  cascadeWarning = "",
  onCascadeConfirm = null, // If provided, shows a separate "Delete All" button
  // Special item blocking props
  warningMessage = null, // Warning message to display (e.g., special items must be deleted first)
  disableConfirm = false, // Disable the confirm button
}) => {
  const hasLinkedItems = linkedItems && linkedItems.length > 0;

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
  };

  const handleCascadeConfirm = async () => {
    if (onCascadeConfirm) {
      await onCascadeConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="delete-confirmation-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-full ${destructive ? 'bg-red-100' : 'bg-amber-100'}`}>
              <AlertTriangle className={`w-6 h-6 ${destructive ? 'text-red-600' : 'text-amber-600'}`} />
            </div>
            <DialogTitle className="text-xl">{title}</DialogTitle>
          </div>
        </DialogHeader>
        
        <div className="py-4">
          <DialogDescription className="text-base text-foreground/80">
            {description}
          </DialogDescription>
          
          {itemName && (
            <div className="mt-4 p-3 bg-muted rounded-lg border">
              <p className="text-sm text-muted-foreground">{itemType}:</p>
              <p className="font-medium text-foreground">{itemName}</p>
            </div>
          )}

          {/* Cascade Delete Warning */}
          {hasLinkedItems && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Link2 className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">
                    This will also delete {linkedItems.length} linked item{linkedItems.length > 1 ? 's' : ''}:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {linkedItems.slice(0, 5).map((item, index) => (
                      <li key={index} className="text-sm text-amber-700 flex items-center gap-1">
                        <Trash2 className="w-3 h-3" />
                        {item.description || item.name || `${item.type}: ${item.id?.slice(0, 8)}`}
                      </li>
                    ))}
                    {linkedItems.length > 5 && (
                      <li className="text-sm text-amber-600 italic">
                        ...and {linkedItems.length - 5} more
                      </li>
                    )}
                  </ul>
                  {cascadeWarning && (
                    <p className="mt-2 text-sm text-amber-700">{cascadeWarning}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Special Items Blocking Warning */}
          {warningMessage && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">Action Required</p>
                  <p className="mt-1 text-sm text-red-700">{warningMessage}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid="delete-cancel-btn"
          >
            {disableConfirm ? 'Close' : 'Cancel'}
          </Button>
          
          {!disableConfirm && (
            hasLinkedItems && onCascadeConfirm ? (
              <Button
                variant="destructive"
                onClick={handleCascadeConfirm}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="delete-cascade-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting All...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete All ({linkedItems.length + 1} items)
                  </>
                )}
              </Button>
            ) : (
              <Button
                variant={destructive ? "destructive" : "default"}
                onClick={handleConfirm}
                disabled={loading}
                className={destructive ? "bg-red-600 hover:bg-red-700" : ""}
                data-testid="delete-confirm-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteConfirmationModal;
